import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { organizzazioni } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Building2, Plus, Edit, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

const TIPI_ORGANIZZAZIONE = [
  { value: "volontari_anc", label: "Volontari ANC", color: "#0B1E3D" },
  { value: "altri_volontari", label: "Altri Volontari", color: "#1E40AF" },
  { value: "civili_ospitati", label: "Civili Ospitati", color: "#059669" },
  { value: "enti_pubblici", label: "Enti Pubblici", color: "#7C3AED" },
  { value: "forze_ordine", label: "Forze dell'Ordine", color: "#0891B2" },
  { value: "autorita", label: "Autorità", color: "#DC2626" },
  { value: "maestranze", label: "Maestranze", color: "#D97706" },
];

const initialFormData = {
  nome: "",
  tipo: "volontari_anc",
  descrizione: "",
  colore_badge: "#0B1E3D",
};

export default function Organizzazioni() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [formData, setFormData] = useState(initialFormData);

  const queryClient = useQueryClient();

  const { data: orgsData, isLoading } = useQuery({
    queryKey: ['organizzazioni'],
    queryFn: organizzazioni.list,
  });

  const createMutation = useMutation({
    mutationFn: organizzazioni.create,
    onSuccess: () => {
      queryClient.invalidateQueries(['organizzazioni']);
      setDialogOpen(false);
      setFormData(initialFormData);
      toast.success("Organizzazione creata");
    },
    onError: (error) => toast.error(error.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => organizzazioni.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['organizzazioni']);
      setDialogOpen(false);
      setSelectedOrg(null);
      setFormData(initialFormData);
      toast.success("Organizzazione aggiornata");
    },
    onError: (error) => toast.error(error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: organizzazioni.delete,
    onSuccess: () => {
      queryClient.invalidateQueries(['organizzazioni']);
      toast.success("Organizzazione eliminata");
    },
    onError: (error) => toast.error(error.message),
  });

  const handleEdit = (org) => {
    setSelectedOrg(org);
    setFormData({
      nome: org.nome,
      tipo: org.tipo,
      descrizione: org.descrizione || "",
      colore_badge: org.colore_badge,
    });
    setDialogOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedOrg) {
      updateMutation.mutate({ id: selectedOrg.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleTipoChange = (tipo) => {
    const tipoConfig = TIPI_ORGANIZZAZIONE.find(t => t.value === tipo);
    setFormData({ 
      ...formData, 
      tipo, 
      colore_badge: tipoConfig?.color || "#0B1E3D" 
    });
  };

  const getTipoLabel = (tipo) => {
    return TIPI_ORGANIZZAZIONE.find(t => t.value === tipo)?.label || tipo;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="organizzazioni-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Organizzazioni</h1>
          <p className="text-slate-500 mt-1">Gestione enti e gruppi nel campo</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setSelectedOrg(null);
            setFormData(initialFormData);
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-[#0B1E3D] hover:bg-[#1E40AF]" data-testid="add-org-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuova Organizzazione
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{selectedOrg ? "Modifica Organizzazione" : "Nuova Organizzazione"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="nome">Nome *</Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  required
                  data-testid="input-org-nome"
                />
              </div>

              <div>
                <Label>Tipo *</Label>
                <Select value={formData.tipo} onValueChange={handleTipoChange}>
                  <SelectTrigger data-testid="select-org-tipo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPI_ORGANIZZAZIONE.map(tipo => (
                      <SelectItem key={tipo.value} value={tipo.value}>
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: tipo.color }} />
                          {tipo.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="colore_badge">Colore Badge</Label>
                <div className="flex gap-2">
                  <Input
                    id="colore_badge"
                    type="color"
                    value={formData.colore_badge}
                    onChange={(e) => setFormData({ ...formData, colore_badge: e.target.value })}
                    className="w-16 h-10 p-1 cursor-pointer"
                  />
                  <Input
                    value={formData.colore_badge}
                    onChange={(e) => setFormData({ ...formData, colore_badge: e.target.value })}
                    className="flex-1 font-mono"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="descrizione">Descrizione</Label>
                <Textarea
                  id="descrizione"
                  value={formData.descrizione}
                  onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annulla
                </Button>
                <Button 
                  type="submit" 
                  className="bg-[#0B1E3D] hover:bg-[#1E40AF]"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  data-testid="submit-org-btn"
                >
                  {createMutation.isPending || updateMutation.isPending ? "Salvataggio..." : "Salva"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Organizations Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B1E3D]"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="orgs-list">
          {orgsData?.map(org => (
            <Card key={org.id} className="hover:shadow-md transition-shadow" data-testid={`org-card-${org.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: org.colore_badge + '20' }}
                    >
                      <Building2 className="h-6 w-6" style={{ color: org.colore_badge }} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{org.nome}</h3>
                      <Badge 
                        variant="outline" 
                        className="text-xs mt-1"
                        style={{ borderColor: org.colore_badge, color: org.colore_badge }}
                      >
                        {getTipoLabel(org.tipo)}
                      </Badge>
                    </div>
                  </div>
                </div>

                {org.descrizione && (
                  <p className="text-sm text-slate-600 mt-3 line-clamp-2">{org.descrizione}</p>
                )}

                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Users className="h-4 w-4" />
                    <span>{org.membri_count || 0} membri</span>
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleEdit(org)}
                      data-testid={`edit-org-${org.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => {
                        if (window.confirm("Sei sicuro di voler eliminare questa organizzazione?")) {
                          deleteMutation.mutate(org.id);
                        }
                      }}
                      data-testid={`delete-org-${org.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {orgsData?.length === 0 && (
            <div className="col-span-full text-center py-12 text-slate-500">
              <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nessuna organizzazione trovata</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
